import { useState, useMemo } from 'react';
import { Search, Plus, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAllTools, useDeleteTool } from '@/hooks/useTools';
import { ToolFormModal } from '@/components/modals/ToolFormModal';
import type { Tables } from '@/integrations/supabase/types';

export function ToolsManagementPanel() {
  const { data: allTools = [], isLoading } = useAllTools();
  const deleteTool = useDeleteTool();

  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTool, setEditingTool] = useState<Tables<'tools'> | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<Tables<'tools'> | null>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return allTools;
    const q = search.toLowerCase();
    return allTools.filter(
      (t) => t.name.toLowerCase().includes(q) || t.category?.toLowerCase().includes(q)
    );
  }, [allTools, search]);

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteTool.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Ferramenta removida');
        setDeleteTarget(null);
      },
      onError: (e: any) => toast.error(e.message),
    });
  };

  const openCreate = () => {
    setEditingTool(undefined);
    setModalOpen(true);
  };

  const openEdit = (tool: Tables<'tools'>) => {
    setEditingTool(tool);
    setModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-secondary">{allTools.length} ferramentas no sistema</span>
        <Button size="sm" onClick={openCreate} className="gap-1.5">
          <Plus size={14} /> Nova Ferramenta
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
        <input
          type="text"
          placeholder="Buscar por nome ou categoria..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full h-9 pl-10 pr-4 rounded-md border border-color bg-surface text-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center space-y-2">
          <span className="text-4xl">🔧</span>
          <p className="text-secondary text-sm font-medium">Nenhuma ferramenta encontrada</p>
        </div>
      ) : (
        <div className="rounded-lg border border-color overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary">
                <TableHead className="w-12"></TableHead>
                <TableHead>Nome</TableHead>
                <TableHead className="w-28">Categoria</TableHead>
                <TableHead className="w-24">Escopo</TableHead>
                <TableHead className="w-48">URL</TableHead>
                <TableHead className="w-32 text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((tool) => (
                <TableRow key={tool.id}>
                  <TableCell>
                    <span className="text-lg">{tool.icon || '🔧'}</span>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-primary text-sm">{tool.name}</span>
                    {tool.description && (
                      <p className="text-xs text-tertiary line-clamp-1 mt-0.5">{tool.description}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-secondary">{tool.category || 'Geral'}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-secondary">
                      {tool.scope_type === 'global' && !tool.project_id && '🏢 Global'}
                      {tool.scope_type === 'personal' && '👤 Pessoal'}
                      {tool.scope_type === 'team' && '👥 Time'}
                      {tool.project_id && '📁 Projeto'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <a
                      href={tool.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-brand-primary hover:underline flex items-center gap-1 truncate max-w-[180px]"
                    >
                      {tool.url.replace(/^https?:\/\//, '').slice(0, 30)}
                      <ExternalLink size={12} className="shrink-0" />
                    </a>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => openEdit(tool)}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(tool)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ToolFormModal open={modalOpen} onOpenChange={setModalOpen} tool={editingTool} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover ferramenta</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover <strong>{deleteTarget?.name}</strong>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteTool.isPending ? 'Removendo...' : 'Remover'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
